/* Repository path: frontend/src/features/journey/profileConditionals.ts */

import type { Answers } from "../../types";

/**
 * The Fast Profile's conditional-visibility rules, extracted verbatim out
 * of `App.tsx` (W4-07) so the first-run questionnaire wizard and the
 * profile editor (`ProfileEditPage`) share exactly one copy rather than
 * two that could drift apart. Mirrors
 * `planner.journey.services.CONDITIONALS` on the server field-for-field;
 * `tests/frontend/features/journey/profileConditionals.test.ts` asserts
 * the two agree.
 */
export function isActive(field: keyof Answers, answers: Answers): boolean {
  if (field === "registrationStatus") return answers.alreadyInNetherlands === "yes";
  if (field === "recruitedBeforeArrival") {
    return answers.activities.some((activity) => ["employee", "paid_intern"].includes(activity));
  }
  if (field === "businessStatus") {
    return answers.activities.includes("freelancer_business_owner");
  }
  if (field === "studentAccommodationFacilities") {
    return answers.housingType === "student_accommodation";
  }
  return true;
}

/** Every conditional field this Fast Profile has, in a stable order —
 * used both to sweep an answer set clean and to check, one at a time,
 * whether a change just revealed one of them. */
export const CONDITIONAL_FIELDS: (keyof Answers)[] = [
  "registrationStatus",
  "recruitedBeforeArrival",
  "businessStatus",
  "studentAccommodationFacilities",
];

/**
 * Blanks every conditional field that `isActive` now says is inactive,
 * leaving every other field untouched. Idempotent: clearing an
 * already-empty field is a no-op, so this is safe to call after every
 * change rather than only the one that could have caused it — which is
 * exactly what lets `App.tsx` and `ProfileEditPage` apply it uniformly
 * instead of re-deriving, per call site, which specific key change could
 * affect which conditional.
 */
export function clearInactive(answers: Answers): Answers {
  const next = { ...answers };
  for (const field of CONDITIONAL_FIELDS) {
    if (!isActive(field, next)) {
      (next[field] as string) = "";
    }
  }
  return next;
}

/**
 * The plain-language line announced to the live region the moment a
 * conditional question first becomes visible. Keyed by the same field
 * names as `CONDITIONAL_FIELDS`.
 */
export const REVEAL_ANNOUNCEMENTS: Partial<Record<keyof Answers, string>> = {
  registrationStatus: "What is your municipality registration status?",
  recruitedBeforeArrival: "Were you recruited before moving to the Netherlands?",
  businessStatus: "What best describes your business?",
  studentAccommodationFacilities: "Does it have its own entrance and essential private facilities?",
};

/**
 * The line announced when an already-answered conditional question is
 * hidden again — distinct wording from a first reveal, and distinct from
 * the wizard's own silent clear, because in the *editor* a hidden
 * question can be discarding a real, previously-saved answer: the user
 * should be told plainly that it will not be used, not just see it
 * vanish. `App.tsx`'s first-run wizard does not need this wording (there
 * is nothing saved yet to lose), so only `ProfileEditPage` uses it.
 */
export function conditionalRemovedAnnouncement(field: keyof Answers, legend: string): string {
  void field;
  return `Question removed: ${legend}. Your earlier answer to it will not be used.`;
}

/**
 * Given the answers before and after one change, the first conditional
 * field (in `CONDITIONAL_FIELDS` order) that just became active, if any —
 * the generalized form of the reveal check `App.tsx` and `ProfileEditPage`
 * both need after calling `clearInactive`.
 */
export function firstNewlyActiveField(
  before: Answers,
  after: Answers,
): (keyof Answers) | undefined {
  return CONDITIONAL_FIELDS.find((field) => !isActive(field, before) && isActive(field, after));
}

/**
 * The conditional fields that were answered before and are hidden now —
 * the generalized form of the "cleared" check the profile editor's
 * pre-save note and post-save summary both need.
 */
export function newlyHiddenAnsweredFields(before: Answers, after: Answers): (keyof Answers)[] {
  return CONDITIONAL_FIELDS.filter((field) => {
    const wasAnswered = Array.isArray(before[field])
      ? (before[field] as unknown[]).length > 0
      : Boolean(before[field]);
    return wasAnswered && isActive(field, before) && !isActive(field, after);
  });
}
